import axios from "axios";

export { TRASH_TYPES, TYPE_INFO, TRASH_FEATURES } from "./trashTypes";

const API = process.env.REACT_APP_API_ROOT;

/**
 * Trash (`/api/admin/trash`) — deleted content, restorable for 30 days.
 *
 * Restore and "delete forever" are addressed by TYPE as well as id, because
 * the right that gates them is that type's `delete` (restoring a book needs
 * books.delete). A selection spanning types is therefore sent as one request
 * per type — see `groupByType`.
 *
 * Same contract as commentsApi: resolves to the response's `data`, throws with
 * the server's own message.
 */
const call = async (request) => {
  try {
    const { data } = await request;
    if (data?.status === false) throw new Error(data.error || data.message || "Something went wrong");
    return data?.data;
  } catch (error) {
    throw new Error(error?.response?.data?.error || error.message || "Something went wrong");
  }
};

export const trashApi = (token) => {
  const opts = { headers: { Authorization: `Bearer ${token}` } };
  return {
    list: (params) => call(axios.get(`${API}/api/admin/trash`, { ...opts, params })),
    // Not through `call`: a restore where every item was refused still has a
    // per-item `failed` list worth showing, and `call` would reduce it to one
    // line of text.
    restore: async (type, trashIds) => {
      try {
        const { data } = await axios.put(`${API}/api/admin/trash/${type}/restore`, { trashIds }, opts);
        return data?.data || { restored: [], failed: [] };
      } catch (error) {
        throw new Error(error?.response?.data?.error || error.message || "Something went wrong");
      }
    },
    purge: (type, trashIds) =>
      call(axios.put(`${API}/api/admin/trash/${type}/delete`, { trashIds }, opts)),
  };
};

/** `Map(trashId -> row)` → `{ book: [ids], post: [ids] }`. */
export const groupByType = (rows) =>
  rows.reduce((groups, row) => {
    (groups[row.type] = groups[row.type] || []).push(row._id);
    return groups;
  }, {});
